import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, FoEmpty, FoTable, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, VendorSelect, useOsResource, useVendorOptions } from "./osForm";
import { pageMeta } from "./osRules";

const blankLine = { sku: "", productName: "", onHandQty: "", unitsPerPallet: "", pallets: "" };

const runBody = (draft) => ({
  vendorId: draft.vendorId,
  period: draft.period,
  freePalletAllowance: Number(draft.freePalletAllowance),
  pricePerPalletDay: Number(draft.pricePerPalletDay),
  billingDays: Number(draft.billingDays),
  lines: draft.lines.filter((line) => line.sku).map((line) => {
    const next = {
      sku: line.sku,
      productName: line.productName,
      onHandQty: Number(line.onHandQty) || 0,
      unitsPerPallet: Number(line.unitsPerPallet) || 0,
    };
    if (line.pallets !== "" && line.pallets != null) next.pallets = Number(line.pallets);
    return next;
  }),
});

const statusMoves = {
  Draft: ["Reviewed"],
  Reviewed: ["Approved", "Draft"],
  Approved: [],
  Invoiced: [],
};

export default function OverstockCalculator() {
  const vendors = useVendorOptions();
  const [draft, setDraft] = useState({
    vendorId: "",
    period: "",
    freePalletAllowance: "2",
    pricePerPalletDay: "10",
    billingDays: "30",
    lines: [{ ...blankLine }],
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);
  const [invoiceDate, setInvoiceDate] = useState("");
  const runs = useOsResource((auth) => os.overstockRuns(auth, { status, page, limit: 20 }), [status, page]);
  const history = useOsResource((auth) => os.overstockHistory(auth), []);
  const pack = pageMeta(runs.data);

  const setLine = (index, patch) => {
    setDraft((current) => ({
      ...current,
      lines: current.lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
    }));
  };

  const loadStock = async () => {
    if (!draft.vendorId) {
      notification.error({ message: "Choose a brand first." });
      return;
    }
    setBusy(true);
    try {
      const stock = await os.overstockStock(runs.token, draft.vendorId);
      const lines = (stock.lines || []).map((line) => ({
        sku: line.sku || "",
        productName: line.productName || "",
        onHandQty: String(line.onHandQty ?? 0),
        unitsPerPallet: "",
        pallets: "",
      }));
      setDraft((current) => ({ ...current, lines: lines.length ? lines : [{ ...blankLine }] }));
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const submit = async (mode) => {
    const body = runBody(draft);
    if (!body.vendorId || !/^\d{4}-\d{2}$/.test(body.period) || !body.lines.length) {
      notification.error({ message: "Brand, period (YYYY-MM), and at least one SKU are required." });
      return;
    }
    if (!Number.isInteger(body.billingDays) || body.billingDays <= 0) {
      notification.error({ message: "Billing days must be a positive whole number." });
      return;
    }
    setBusy(true);
    try {
      if (mode === "preview") {
        setPreview(await os.overstockPreview(runs.token, body));
      } else {
        await os.saveOverstockRun(runs.token, body);
        notification.success({ message: "Draft run saved." });
        setPreview(null);
        runs.reload();
        history.reload();
      }
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const move = async (id, next) => {
    try {
      await os.overstockStatus(runs.token, id, next);
      notification.success({ message: `Run marked ${next}.` });
      runs.reload();
      history.reload();
    } catch (err) {
      /* toast already shown */
    }
  };

  const invoice = async () => {
    if (!detail?._id) return;
    setBusy(true);
    try {
      await os.overstockInvoice(runs.token, detail._id, invoiceDate ? { dueDate: invoiceDate } : {});
      notification.success({ message: "Draft invoice created." });
      setDetail(null);
      runs.reload();
      history.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const previewLines = preview?.lines || preview?.run?.lines || [];
  const previewTotal = preview?.totalFee ?? preview?.run?.totalFee;

  return (
    <div className="fo-stack">
      <FoCard title="New run" note="One open run per brand per month. Free pallets are applied to the largest pallet lines first. Preview does not save.">
        <div className="fo-form-grid">
          <VendorSelect vendors={vendors} value={draft.vendorId} onChange={(vendorId) => setDraft({ ...draft, vendorId })} emptyLabel="Select a brand" />
          <TextField label="Period" value={draft.period} onChange={(period) => setDraft({ ...draft, period })} hint="YYYY-MM" />
          <TextField label="Free pallets" type="number" value={draft.freePalletAllowance} onChange={(freePalletAllowance) => setDraft({ ...draft, freePalletAllowance })} />
          <TextField label="Price / pallet / day" type="number" value={draft.pricePerPalletDay} onChange={(pricePerPalletDay) => setDraft({ ...draft, pricePerPalletDay })} />
          <TextField label="Billing days" type="number" value={draft.billingDays} onChange={(billingDays) => setDraft({ ...draft, billingDays })} />
        </div>
        <div className="fo-tier-acts" style={{ margin: "14px 0" }}>
          <button type="button" className="fo-btn" disabled={busy} onClick={loadStock}>Load warehouse stock</button>
          <button type="button" className="fo-btn" onClick={() => setDraft({ ...draft, lines: [...draft.lines, { ...blankLine }] })}>Add line</button>
        </div>
        <FoTable
          pagination={false}
          rowKey={(row, index) => `${row.sku}-${index}`}
          dataSource={draft.lines}
          empty="Add a SKU line."
          columns={[
            { title: "SKU", key: "sku", render: (_, row, index) => <input className="fo-control" value={row.sku} onChange={(event) => setLine(index, { sku: event.target.value })} /> },
            { title: "Product", key: "product", render: (_, row, index) => <input className="fo-control" value={row.productName} onChange={(event) => setLine(index, { productName: event.target.value })} /> },
            { title: "On hand", key: "qty", render: (_, row, index) => <input className="fo-control" type="number" value={row.onHandQty} onChange={(event) => setLine(index, { onHandQty: event.target.value })} /> },
            { title: "Units / pallet", key: "units", render: (_, row, index) => <input className="fo-control" type="number" value={row.unitsPerPallet} onChange={(event) => setLine(index, { unitsPerPallet: event.target.value })} /> },
            { title: "Pallets override", key: "pallets", render: (_, row, index) => <input className="fo-control" type="number" value={row.pallets} placeholder="Optional" onChange={(event) => setLine(index, { pallets: event.target.value })} /> },
          ]}
        />
        <div className="fo-tier-acts" style={{ marginTop: 16 }}>
          <button type="button" className="fo-btn" disabled={busy} onClick={() => submit("preview")}>Preview</button>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={() => submit("save")}>Save draft</button>
        </div>
        {preview && (
          <div style={{ marginTop: 18 }}>
            <strong>Preview total {formatEgp(previewTotal)} EGP</strong>
            <FoTable
              pagination={false}
              rowKey={(row, index) => `${row.sku}-${index}`}
              dataSource={previewLines}
              empty="The preview returned no lines."
              columns={[
                { title: "SKU", dataIndex: "sku" },
                { title: "Pallets", dataIndex: "pallets" },
                { title: "Free applied", dataIndex: "freePalletsApplied" },
                { title: "Overstock", dataIndex: "overstockQty" },
                { title: "Line fee", dataIndex: "lineFee", render: (value) => `${formatEgp(value)} EGP` },
              ]}
            />
          </div>
        )}
      </FoCard>

      <div className="fo-tools">
        <SelectField label="Run status" value={status} onChange={(value) => { setPage(1); setStatus(value); }} allowEmpty options={["Draft", "Reviewed", "Approved", "Invoiced"]} />
      </div>
      <FoCard title="Runs">
        <PagerTable
          loading={runs.loading}
          dataSource={pack.docs}
          rowKey="_id"
          page={page}
          limit={20}
          total={pack.total}
          onChange={(nextPage) => setPage(nextPage)}
          empty="No overstock runs yet."
          columns={[
            { title: "Period", dataIndex: "period" },
            { title: "Brand", dataIndex: "brandName", render: (name, row) => name || row.vendor?.brandName || row.vendorId },
            { title: "Total", dataIndex: "totalFee", render: (value) => `${formatEgp(value)} EGP` },
            { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            {
              title: "",
              key: "actions",
              render: (_, run) => (
                <div className="fo-tier-acts">
                  {(statusMoves[run.status] || []).map((next) => (
                    <button key={next} type="button" className="fo-btn" onClick={() => move(run._id, next)}>{next}</button>
                  ))}
                  {run.status === "Approved" && <button type="button" className="fo-btn fo-btn-primary" onClick={() => { setInvoiceDate(""); setDetail(run); }}>Invoice</button>}
                </div>
              ),
            },
          ]}
        />
      </FoCard>
      <FoCard title="History">
        {history.loading && <FoEmpty>Loading history…</FoEmpty>}
        {!history.loading && (
          <FoTable
            rowKey="period"
            dataSource={history.data?.history || []}
            pagination={false}
            empty="No overstock history yet."
            columns={[
              { title: "Period", dataIndex: "period" },
              { title: "Brands", dataIndex: "brandCount" },
              { title: "Runs", dataIndex: "runCount" },
              { title: "Total fee", dataIndex: "totalFee", render: (value) => `${formatEgp(value)} EGP` },
              { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            ]}
          />
        )}
      </FoCard>
      <FoDialog open={Boolean(detail)} title="Create draft invoice" onClose={() => !busy && setDetail(null)}>
        <p className="fo-note">This creates a draft register invoice for {formatEgp(detail?.totalFee)} EGP and marks the run Invoiced. Only an Approved run can be invoiced.</p>
        <TextField label="Due date" type="date" value={invoiceDate} onChange={setInvoiceDate} hint="Optional. The invoice defaults to 14 days out." />
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={invoice}>Create invoice</button>
        </div>
      </FoDialog>
    </div>
  );
}
